import React, { useState, useEffect, useCallback, useMemo } from 'react';
import adler32 from 'adler-32';
import { Col, Div, Icon, Input, Label, Row, Switch, Text } from '@pinpoint/design-kit';

import CONSTANTS from 'src/store/constants';
import { Card, FileDrop, StepperProgress, Button } from 'src/components';

/* eslint-disable react/prop-types */

const TOTAL_PAGES = 10;

export function TopButtons() {
  return (
    <Row p={{ b: '1.25rem' }}>
      <Col size="12">
        <Div m={{ t: '1rem' }} d="flex" justify="flex-start">
          <Button
            buttonStyle="primary"
            onClick={() => {
              window.open('/static/Pinpoint_Predictive_Actuarial_Requirements_19-VI-24.pdf');
            }}
            data-tip="Download Actuarial requirements"
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon name="Download2" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size="0.875rem" />
            ACTUARIAL REQUIREMENTS
          </Button>
          <Button
            buttonStyle="primary"
            onClick={() => {
              window.open('/static/Pinpoint_Actuarial_Onboarding_Template_Updated_18-I-24.xlsx');
            }}
            data-tip="Download Actuarial template"
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon name="Download2" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size="0.875rem" />
            ACTUARIAL TEMPLATE
          </Button>
        </Div>
      </Col>
    </Row>
  );
}

function BottomButtons({ onBack, onNext, isNextDisabled, isNextShown, isSkipReqShown, onSkipReq }) {
  return (
    <Row>
      <Col size="12">
        <Div m={{ t: '1rem', x: '-0.5rem' }} d="flex" justify="space-between">
          <Button
            buttonStyle="primary"
            m={{ l: '0.5rem' }}
            onClick={() => {
              onBack();
            }}
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon color="white" name="LongLeft" size="1rem" m={{ r: '0.5rem' }} />
            BACK
          </Button>
          <Div d="flex">
            {isSkipReqShown ? (
              <Button
                buttonStyle="primary"
                onClick={onSkipReq}
                style={{
                  flex: '0 0 auto',
                }}
              >
                SKIP REQUIREMENTS
                <Icon color="white" name="DoubleArrow" size="0.75rem" m={{ l: '0.5rem' }} viewBox="0 0 12 12" />
              </Button>
            ) : null}
            {isNextShown ? (
              <Button
                buttonStyle="primary"
                onClick={() => {
                  onNext();
                }}
                style={{
                  flex: '0 0 auto',
                }}
                disabled={isNextDisabled}
              >
                NEXT
                <Icon color="white" name="LongRight" size="1rem" m={{ l: '0.5rem' }} />
              </Button>
            ) : null}
          </Div>
        </Div>
      </Col>
    </Row>
  );
}

function ModelOverview({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Actuarial model overview');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  return (
    <Text>
      For Actuarial Loss Predictions, you will upload only one dataset for each peril, or set of perils, that you are
      modeling; please confirm which perils you&apos;re modeling with Pinpoint prior to data onboarding. Each dataset,
      in turn, allows you to build one or more of the following models: claim frequency, severity, and two types of
      loss-cost predictions (multiplied, and direct).
    </Text>
  );
}

function LookbackWindow({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Lookback window');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  return (
    <Text>
      Select all customers active at any point over the past 4 years, as well as their relevant loss data (described in
      the requirements) corresponding to this same lookback period. This dataset may include up to 1,000,000 Records.
    </Text>
  );
}

function DatasetBreakdown({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Dataset breakdown');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  return (
    <Text>
      Within each Actuarial Loss dataset, you will include three types of Records: (1) Training Records with claims, (2)
      Training Records with no claims, and (3) blind Validation Records whose loss values will not be revealed in the
      dataset. The following sections describe in greater detail each of these three types of Records within the
      Actuarial dataset.
    </Text>
  );
}

function TrainingRecordsClaims({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Training records with claims');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  const styles = useMemo(
    () => ({
      customColumnName: {
        fontWeight: 'bold',
        fontStyle: 'italic',
      },
    }),
    []
  );

  return (
    <Text tag="ul" m={{ l: '1rem' }} style={{ listStyle: 'outside' }}>
      <li>
        The following fields are always required for Records with claims:
        <ul style={{ listStyle: 'circle', paddingLeft: 'revert' }}>
          <li>
            <strong style={styles.customColumnName}>TG_SumLossCounts</strong>
          </li>
          <li>
            <strong style={styles.customColumnName}>TG_SumPremiumsEarned</strong>
          </li>
          <li>
            <strong style={styles.customColumnName}>TG_SumLossDollarsAndExpenses</strong>
          </li>
          <li>
            <strong style={styles.customColumnName}>TG_SumPolicyPeriods</strong>
          </li>
        </ul>
      </li>
      <li>
        The values in <strong style={styles.customColumnName}>TG_SumPolicyPeriods</strong> must be non-zero positive
        numbers, where 1.0 units equal six months for all Personal Auto policies, and twelve months for all other Lines
        of Business. Given the four-year look-back period, the maximum value in this field should be 8.0 for Personal
        Auto and 4.0 for all other lines.
      </li>
      <li>
        The column <strong style={styles.customColumnName}>TG_OtherExposure</strong> should be deleted for most Personal
        Lines applications. However, this column is required for most Commercial Lines applications; C/L actuaries may
        choose to use manual premium multiplied by <strong style={styles.customColumnName}>TG_SumPolicyPeriods</strong>{' '}
        as the value for
        <strong style={styles.customColumnName}>TG_OtherExposure</strong>, to account for business size and class,
        because Pinpoint&apos;s Predictions account only for the risk of the individual owner/operator - not for
        business features like headcount or class code.
      </li>
      <li>
        Please ensure that all values in <strong style={styles.customColumnName}>TG_SumPolicyPeriods</strong> (and in{' '}
        <strong style={styles.customColumnName}>TG_OtherExposure</strong>, where relevant) are non-zero positive
        numbers.
      </li>
      <li>Do not include any dollar signs or negative numbers anywhere.</li>
    </Text>
  );
}

function TrainingRecordsNoClaims({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Training records with no claims');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  const styles = useMemo(
    () => ({
      customColumnName: {
        fontWeight: 'bold',
        fontStyle: 'italic',
      },
    }),
    []
  );

  return (
    <Text>
      <strong style={{ fontWeight: 'bold' }}>Loss Columns:</strong> The non-claims Training Records must have a zero (0)
      entered in the <strong style={styles.customColumnName}>TG_SumLossCounts</strong> column. Please also fill in
      values for <strong style={styles.customColumnName}>TG_SumLossDollarsAndExpenses</strong> (entering zeros where
      needed), <strong style={styles.customColumnName}>TG_SumPremiumsEarned</strong>, and{' '}
      <strong style={styles.customColumnName}>TG_SumPolicyPeriods</strong> in all cases, and{' '}
      <strong style={styles.customColumnName}>TG_OtherExposure</strong> where relevant. Do not include any dollar signs
      or negative numbers anywhere.
    </Text>
  );
}

function BlindValidation({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Blind validation records');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  return (
    <Text>
      <Text>
        <strong style={{ fontWeight: 'bold' }}>Data Volumes:</strong> Please also ensure that 20% of the Records in your
        Actuarial Loss dataset are blind Validation Records. These Records must be mutually exclusive from, but fully
        representative of, the claims and non-claims Training Records described earlier.
      </Text>
      <br />
      <Text>
        <strong style={{ fontWeight: 'bold' }}>Loss Columns:</strong> Validation Records&apos; values in the loss
        columns must be left as empty strings (i.e. no values entered in these cells) and not zeros, to be recognized by
        our platform as Blind Validation Records. However, entering actual values into{' '}
        <strong style={{ fontWeight: 'bold', fontStyle: 'italic' }}>TG_SumPremiumsEarned</strong> is recommended for
        Validation Records.
      </Text>
    </Text>
  );
}

function ProportionRecords({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Proportion of records');
    disableNext(false);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  return (
    <Text>
      The breakdown of claims vs. no claims Records, in both the Training Set and blind Validation Set should be
      proportional to their frequency in your book over the 4-year look-back period.
    </Text>
  );
}

function OnboardingChecklist1({ setTitle, disableNext, showNext }) {
  const leftItems = [
    'The only headers in my .csv are exactly the same as in the onboarding template.',
    "If certain PII are not available for specific fields, they've been left as empty strings.",
    'Each person record is present in only one row and has a unique value under the "CRM ID" header; multiple policy periods corresponding to the same person have been collapsed into a single row.',
  ];
  const rightItems = [
    "Where relevant, Address1 and Address2 fields are concatenated with a space between them, and placed in the 'Address' column.",
    'All state names are two-letter abbreviations.',
    'ZIP and ZIP+4 are in separate columns. ZIP is always 5 digits long (with an initial zero if necessary), and ZIP+4 is always 4 digits long.',
  ];

  const [responses, setResponses] = useState(new Array(6).fill(false));

  useEffect(() => {
    setTitle('Pre-onboarding checklist');
    disableNext(true);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  useEffect(() => {
    disableNext(!responses.every((x) => x));
  }, [responses, disableNext]);

  return (
    <Div>
      <Row>
        <Col size="6">
          {leftItems.map((item, index) => (
            <Div d="flex" m={{ y: '1rem' }} key={`left.${adler32.str(leftItems[index])}`}>
              <Label
                tag="div"
                align="center"
                data-test={`left.${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setResponses((resp) => {
                    const nextResp = [...resp];
                    nextResp[index] = !resp[index];
                    return nextResp;
                  });
                }}
              >
                <Switch checked={responses[index]} activeColor="brand500" activeShadow="0" />
              </Label>
              <Text>{item}</Text>
            </Div>
          ))}
        </Col>
        <Col size="6">
          {rightItems.map((item, index) => (
            <Div d="flex" m={{ y: '1rem' }} key={`right.${adler32.str(rightItems[index])}`}>
              <Label
                tag="div"
                align="center"
                data-test={`right.${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setResponses((resp) => {
                    const nextResp = [...resp];
                    nextResp[index + leftItems.length] = !resp[index + leftItems.length];
                    return nextResp;
                  });
                }}
              >
                <Switch checked={responses[index + leftItems.length]} activeColor="brand500" activeShadow="0" />
              </Label>
              <Text>{item}</Text>
            </Div>
          ))}
        </Col>
      </Row>
    </Div>
  );
}

function OnboardingChecklist2({ setTitle, disableNext, showNext }) {
  const leftItems = [
    'Phone values have exactly 10 digits with no special characters.',
    'Dollar signs are not included anywhere.',
  ];
  const rightItems = [
    'Values in column R (and in column S, where relevant) are all non-zero positive numbers.',
    'My file is a UTF-8 encoded .csv. It has a transparent name and a .csv extension.',
  ];

  const [responses, setResponses] = useState(new Array(2).fill(false));

  useEffect(() => {
    setTitle('Pre-onboarding checklist');
    disableNext(true);
    showNext(true);
  }, [setTitle, disableNext, showNext]);

  useEffect(() => {
    disableNext(!responses.every((x) => x));
  }, [responses, disableNext]);

  return (
    <Div>
      <Row>
        <Col size="6">
          {leftItems.map((item, index) => (
            <Div d="flex" m={{ y: '1rem' }} key={`left.${adler32.str(leftItems[index])}`}>
              <Label
                tag="div"
                align="center"
                data-test={`left.${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setResponses((resp) => {
                    const nextResp = [...resp];
                    nextResp[index] = !resp[index];
                    return nextResp;
                  });
                }}
              >
                <Switch checked={responses[index]} activeColor="brand500" activeShadow="0" />
              </Label>
              <Text>{item}</Text>
            </Div>
          ))}
        </Col>
        <Col size="6">
          {rightItems.map((item, index) => (
            <Div d="flex" m={{ y: '1rem' }} key={`right.${adler32.str(rightItems[index])}`}>
              <Label
                tag="div"
                align="center"
                data-test={`right.${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setResponses((resp) => {
                    const nextResp = [...resp];
                    nextResp[index + leftItems.length] = !resp[index + leftItems.length];
                    return nextResp;
                  });
                }}
              >
                <Switch checked={responses[index + leftItems.length]} activeColor="brand500" activeShadow="0" />
              </Label>
              <Text>{item}</Text>
            </Div>
          ))}
        </Col>
      </Row>
    </Div>
  );
}

function FileUpload({
  setTitle,
  datasetTag,
  handleFileDrop,
  handleFileSelect,
  files,
  stageFiles,
  progress,
  uploadFileStatus,
  datasetName,
  isAdmin,
  compState,
  triggerDetWorkflowStatus,
  // functions
  getTagStyles,
  setDatasetName,
  uploadFiles,
  setCompState,
  showNext,
}) {
  useEffect(() => {
    setTitle('Select file to upload');
    showNext(false);
  }, [setTitle, showNext]);

  return (
    <Div w="100%">
      <Row>
        <Col size="5">
          <FileDrop
            onDrop={handleFileDrop}
            onSelect={handleFileSelect}
            droppedFiles={files}
            setDroppedFiles={stageFiles}
            progress={progress}
            uploadFileStatus={uploadFileStatus}
          />
        </Col>
        <Col size="7">
          Dataset Name
          <Input
            value={datasetName}
            onChange={(e) => setDatasetName(e.target.value)}
            h="1.875rem"
            m={{ b: '.875rem', t: '.25rem' }}
          />
          Type
          <Div
            d="flex"
            align="center"
            border="1px solid"
            borderColor="gray500"
            rounded="md"
            p={{ x: '.75rem' }}
            m={{ b: '.875rem', t: '.25rem' }}
            h="1.875rem"
          >
            <Icon size=".875rem" m={{ r: '.5em' }} alignSelf="center" {...getTagStyles(datasetTag)} />
            <Text textSize="body" textColor={getTagStyles(datasetTag).color}>
              {CONSTANTS.SEGMENT.TAG_NAME[datasetTag]}
            </Text>
          </Div>
          <Div m={{ t: '2.5rem' }} d="flex" justify="flex-end">
            <Button
              buttonStyle="primary"
              m={{ r: '0rem' }}
              isLoading={compState !== CONSTANTS.COMPONENT.VALIDATION.VALIDATED}
              disabled={
                !isAdmin ||
                uploadFileStatus === CONSTANTS.API.STARTED ||
                triggerDetWorkflowStatus === CONSTANTS.API.STARTED ||
                files.length === 0 ||
                !datasetName ||
                compState !== CONSTANTS.COMPONENT.VALIDATION.VALIDATED
              }
              loading={uploadFileStatus === CONSTANTS.API.STARTED}
              onClick={() => {
                setCompState(CONSTANTS.COMPONENT.OPERATION.STARTED);
                uploadFiles(files, CONSTANTS.S3_PREFIX.ONPOINT_WORKFLOW);
              }}
            >
              UPLOAD
              <Icon name="UploadForward" viewBox="0 0 14 16" color="white" m={{ l: '0.5rem' }} size="0.875rem" />
            </Button>
          </Div>
        </Col>
      </Row>
    </Div>
  );
}

const renderComponentByPage = ({ ...rest }) => {
  const list = [
    <ModelOverview {...rest} />,
    <LookbackWindow {...rest} />,
    <DatasetBreakdown {...rest} />,
    <TrainingRecordsClaims {...rest} />,
    <TrainingRecordsNoClaims {...rest} />,
    <BlindValidation {...rest} />,
    <ProportionRecords {...rest} />,
    <OnboardingChecklist1 {...rest} />,
    <OnboardingChecklist2 {...rest} />,
    <FileUpload {...rest} />,
  ];
  if (rest.page && rest.page > list.length && rest.page > 0) {
    return null;
  }
  return list[rest.page];
};

function Actuarial({ onDone, goBack, ...props }) {
  const [page, setPage] = useState(0);
  const [isNextDisabled, disableNext] = useState(false);
  const [isNextShown, showNext] = useState(true);
  const [isSkipReqShown, setIsSkipReqShown] = useState(+localStorage.getItem('reviewedActuarialInstr'));
  const [title, setTitle] = useState('Actuarial model overview');
  const { setDatasetTag, triggerDetWorkflowStatus } = props;

  const handleSkipReq = useCallback(() => setPage(TOTAL_PAGES - 1), []);

  const onSkip = useCallback(() => {
    if (page < TOTAL_PAGES - 1) return setPage(page + 1);
    return onDone();
  }, [page, onDone]);

  useEffect(() => {
    const nextTag = CONSTANTS.SEGMENT.TAG.ACTUARIAL;
    setDatasetTag(nextTag);
    if (TOTAL_PAGES - 1 === page) localStorage.setItem('reviewedActuarialInstr', '1');
    if (page === TOTAL_PAGES - 1) setIsSkipReqShown(0);
    else setIsSkipReqShown(+localStorage.getItem('reviewedActuarialInstr'));
  }, [page, setIsSkipReqShown, setDatasetTag]);

  useEffect(() => {
    if (triggerDetWorkflowStatus === CONSTANTS.API.SUCCEEDED) onSkip();
  }, [triggerDetWorkflowStatus, onSkip]);

  return (
    <Div>
      <TopButtons />
      <Card
        title={title}
        iconProps={{
          name: 'ActuarialModel',
          color: 'white',
          size: '3.125rem',
          viewBox: '0 0 12 12',
        }}
        titleProps={{
          bg: 'teal',
          h: '5.5rem',
          p: { l: '2.5rem', r: '2.625rem' },
          rounded: { tr: 'md', tl: 'md' },
        }}
        titleTextProps={{
          textColor: 'white',
          textSize: 'title',
          lineHeight: 'title',
        }}
        cardProps={{
          p: '0',
          textSize: 'paragraph',
          style: {
            lineHeight: '1.5rem',
          },
          rounded: 'md',
          textColor: 'shade66',
        }}
      >
        <Row m="0" minH="18rem" p={{ l: '2.5rem', r: '2.5rem', t: '1.75rem', b: '1.875rem' }}>
          {renderComponentByPage({ page, setTitle, disableNext, showNext, onSkip, ...props })}
        </Row>
      </Card>
      <StepperProgress currentPage={page} totalPages={TOTAL_PAGES} />
      <BottomButtons
        onBack={() => {
          if (page > 0) {
            setPage((currPage) => currPage - 1);
          } else {
            goBack();
          }
        }}
        onNext={onSkip}
        isNextDisabled={isNextDisabled}
        isNextShown={isNextShown}
        isSkipReqShown={isSkipReqShown}
        onSkipReq={handleSkipReq}
      />
    </Div>
  );
}

export default Actuarial;
